import { useEffect, useRef, useState } from "react";

declare global {
  interface Window {
    adsbygoogle: { [key: string]: unknown }[];
  }
}

interface GoogleAdProps {
  client: string;
  slot: string;
  format?: string;
  responsive?: string;
  layout?: string;
  style?: React.CSSProperties;
  className?: string;
  onAdFailed?: () => void;
}

const GoogleAd = ({
  client,
  slot,
  format = "auto",
  responsive = "true",
  layout,
  style,
  className,
  onAdFailed,
}: GoogleAdProps) => {
  const adRef = useRef<HTMLModElement>(null);
  const adPushedRef = useRef(false);
  const adFailedRef = useRef(false);
  const [isHiddenContainer, setIsHiddenContainer] = useState(false);

  // Remove the ins element before paint if it's inside a display:none ancestor.
  // adsbygoogle.push({}) scans ALL uninitialized ins elements on the page, so any
  // hidden ins (from non-visible layout variants) would cause availableWidth=0 errors.
  useEffect(() => {
    const el = adRef.current;
    if (!el) return;
    let current: HTMLElement | null = el.parentElement;
    while (current && current !== document.body) {
      if (window.getComputedStyle(current).display === "none") {
        setIsHiddenContainer(true);
        return;
      }
      current = current.parentElement;
    }
  }, []);

  useEffect(() => {
    if (!adRef.current) return;

    let rafId: number | null = null;
    let resizeObserver: ResizeObserver | null = null;
    let statusObserver: MutationObserver | null = null;
    let emptyTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let retryTimeoutId: ReturnType<typeof setTimeout> | null = null;
    let hasIntersected = false;
    let isCleanedUp = false;

    const notifyAdFailed = () => {
      if (adFailedRef.current) return;
      adFailedRef.current = true;
      if (onAdFailed) onAdFailed();
    };

    const hasRenderableSize = (el: HTMLElement) => {
      if (!el.isConnected || !document.body.contains(el)) return false;

      const elStyle = window.getComputedStyle(el);
      if (
        elStyle.display === "none" ||
        elStyle.visibility === "hidden" ||
        (el.offsetParent === null && elStyle.position !== "fixed")
      ) {
        return false;
      }

      const elRect = el.getBoundingClientRect();
      if (elRect.width <= 0 || el.clientWidth <= 0) return false;

      let current: HTMLElement | null = el.parentElement;
      while (current && current !== document.body) {
        const style = window.getComputedStyle(current);
        if (style.display === "none" || style.visibility === "hidden") return false;

        const rect = current.getBoundingClientRect();
        if (rect.width <= 0 || current.clientWidth <= 0) return false;
        current = current.parentElement;
      }

      return true;
    };

    const scheduleRetry = () => {
      if (isCleanedUp || adPushedRef.current || retryTimeoutId) return;
      retryTimeoutId = setTimeout(() => {
        retryTimeoutId = null;
        tryPushWhenSized();
      }, 250);
    };

    const pushAd = () => {
      const el = adRef.current;
      if (!el || isCleanedUp || adPushedRef.current) return false;
      if (!hasRenderableSize(el)) return false;
      try {
        (window.adsbygoogle = window.adsbygoogle || []).push({});
        adPushedRef.current = true;
        return true;
      } catch (e) {
        const message = e instanceof Error ? e.message : String(e);
        if (message.includes("No slot size for availableWidth=0")) {
          scheduleRetry();
          return false;
        }
        notifyAdFailed();
        return false;
      }
    };

    const tryPushWhenSized = (width?: number) => {
      if (!hasIntersected || adPushedRef.current || isCleanedUp) return;
      const el = adRef.current;
      if (!el) return;

      const nextWidth = typeof width === "number" ? width : el.getBoundingClientRect().width;
      if (nextWidth <= 0) {
        scheduleRetry();
        return;
      }

      const didPush = pushAd();
      if (!didPush) {
        scheduleRetry();
      }
    };

    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !adPushedRef.current) {
            hasIntersected = true;
            observer.disconnect();

            if (rafId !== null) {
              cancelAnimationFrame(rafId);
            }
            rafId = requestAnimationFrame(() => {
              rafId = null;
              tryPushWhenSized();
            });
          }
        });
      },
      { threshold: 0 }
    );

    observer.observe(adRef.current);

    if (typeof ResizeObserver !== "undefined") {
      resizeObserver = new ResizeObserver((entries) => {
        const width = entries[0]?.contentRect?.width ?? 0;
        tryPushWhenSized(width);
      });
      resizeObserver.observe(adRef.current);
    }

    const currentAdElement = adRef.current;
    const checkAdStatus = () => {
      const status = currentAdElement.getAttribute("data-ad-status");
      if (status === "unfilled") {
        notifyAdFailed();
      }
      if (status === "filled") {
        adPushedRef.current = true;
      }
    };

    checkAdStatus();

    if (typeof MutationObserver !== "undefined") {
      statusObserver = new MutationObserver(() => {
        checkAdStatus();
      });
      statusObserver.observe(currentAdElement, {
        attributes: true,
        attributeFilter: ["data-ad-status"],
      });
    }

    // Ad blockers or network issues can leave slots without status forever.
    emptyTimeoutId = setTimeout(() => {
      const status = currentAdElement.getAttribute("data-ad-status");
      if (!status) {
        notifyAdFailed();
      }
    }, 10000);

    return () => {
      isCleanedUp = true;
      observer.disconnect();
      resizeObserver?.disconnect();
      statusObserver?.disconnect();
      if (emptyTimeoutId) clearTimeout(emptyTimeoutId);
      if (retryTimeoutId) clearTimeout(retryTimeoutId);
      if (rafId !== null) cancelAnimationFrame(rafId);
    };
  }, []);

  if (isHiddenContainer) return null;

  return (
    <ins
      ref={adRef}
      className={`adsbygoogle ${className || ""}`}
      style={style || { display: "block" }}
      data-ad-client={client}
      data-ad-slot={slot}
      data-ad-format={format}
      data-full-width-responsive={responsive}
      {...(layout ? { "data-ad-layout": layout } : {})}
    />
  );
};

export default GoogleAd;
